import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Grid,
  Typography,
  Button,
} from "@mui/material";
import Alert from "@mui/material/Alert"; // Import Alert for Snackbar
import useFirestore from "../../../../config/fireBaseHook"; // Import useFirestore
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";
import ConfirmDeleteDialog from "../../../common/ConfirmDeleteDialog";

const Edit = ({ open, onClose, statusCodeDetail, onRecordUpdated }) => {
  // Renamed 'course' prop to 'statusCodeDetail'
  const { updateData, deleteData } = useFirestore("ResidenceStatusCodes"); // Specify the collection name
  const [formData, setFormData] = useState({
    id: "", // To store Firestore document ID
    Residence_Status: "",
    Residence_Status_Code: "",
  });
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.id) {
      console.error("Error: Document ID is missing for update.");
      setSnackbarMessage("Error: Document ID is missing for update.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    try {
      // Pass the document ID and the updated data
      await updateData(formData.id, formData);
      setLoading(false);
      setSnackbarMessage("Record has been updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      if (onRecordUpdated) onRecordUpdated(); // Call the callback function
      onClose(); // Close the dialog after successful update
    } catch (error) {
      console.error("Error updating record:", error);
      setLoading(false);
      setSnackbarMessage("Error updating record.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteData(statusCodeDetail.id); // Use the ID from the prop for deletion
      setLoading(false);
      setSnackbarMessage("Record has been deleted successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      onRecordUpdated(); // Call the callback function to refresh the list
      onClose(); // Close the dialog
    } catch (error) {
      console.error("Error deleting record:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initialize form data when the dialog opens or statusCodeDetail changes
    if (statusCodeDetail) {
      setFormData({
        id: statusCodeDetail.id || "", // Ensure ID is captured
        Residence_Status: statusCodeDetail.Residence_Status || "",
        Residence_Status_Code: statusCodeDetail.Residence_Status_Code || "",
      });
    }
  }, [statusCodeDetail]); // Depend on statusCodeDetail prop

  const handleConfirmDeleteOpen = () => {
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDeleteClose = () => {
    setConfirmDeleteOpen(false);
  };

  const handleConfirmDelete = () => {
    handleConfirmDeleteClose();
    handleDelete();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // The X (and backdrop/Escape, which share the same Dialog onClose) always
  // asks for confirmation first rather than closing immediately, so an
  // in-progress edit isn't lost by an accidental click.
  const handleCloseAttempt = () => {
    setConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    setConfirmCloseOpen(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleCloseAttempt} maxWidth="md" fullWidth>
        <ModalDialogTitle onClose={handleCloseAttempt}>
          Edit Record
        </ModalDialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Residence Status"
                name="Residence_Status"
                value={formData.Residence_Status}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
              <TextField
                label="Residence Status Code"
                name="Residence_Status_Code"
                value={formData.Residence_Status_Code}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <ModalActions
          onSave={handleSubmit}
          saving={loading}
          onDelete={handleConfirmDeleteOpen}
          deleting={loading}
        />
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          message="Record has been updated"
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          ContentProps={{
            sx: {
              backgroundColor: "black",
              color: "white",
            },
          }}
        />
      </Dialog>
      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onClose={handleConfirmDeleteClose}
        onConfirm={handleConfirmDelete}
        deleting={loading}
        message="Are you sure you want to delete this?"
      />
      <Dialog
        open={confirmCloseOpen}
        onClose={handleCancelClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Are you sure you want to close before saving your changes?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelClose}>No</Button>
          <Button onClick={handleConfirmClose} variant="contained" color="error">
            Yes, Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Edit;
